import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import { Filter, Calendar, Clock, AlertTriangle, ArrowUp, ArrowUpDown, BarChart3, Copy, Check, ChevronDown, BookOpen, Search, Info, Pencil, Scale, Quote } from 'lucide-react';
import { timelineApi } from '../../services/api';
import { TemporalGap, EventConflict, FilterOptions, RAGCitation } from '../../types';
import { MarkdownWithCitations } from '../citations/MarkdownWithCitations';
import { fromRAGCitation } from '../../types/citation';
import { formatDate } from '../../utils/dateFormat';
import { formatClinicalDescription } from '../../utils/formatDescription';
import { useDocumentViewer } from '../../contexts/DocumentViewerContext';
import { ChronologySidebar } from '../ChronologySidebar';
import { StickyMonthHeader } from '../StickyMonthHeader';
import { ChronologyNarrativeView } from '../ChronologyNarrativeView';
import { RedactionText } from '../redaction/RedactionText';
import { Tooltip } from '../Tooltip';
import { useKeyFindings } from '../../contexts/KeyFindingsContext';
import { EntryActions } from '../ask/EntryActions';
import { CorrectionReasonModal, EVENT_TYPE_OPTIONS, EditedFields } from '../review/InlineEventEditForm';
import { OriginalContentPopover } from '../review/OriginalContentPopover';
import { AnnotatedEventText } from '../review/AnnotatedEventText';
import { chronologyApi, EventAnnotation } from '../../services/chronologyApi';
import { parsePiiMarkers } from '../../utils/parsePiiMarkers';
import { TextSelectionPopover } from '../TextSelectionPopover';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import {
  formatChronologyEvent, formatChronologySummaries, formatFullChronology,
  formatChronologyEventHtml, formatChronologySummariesHtml, formatFullChronologyHtml,
  formatChronologyTablePlain, formatChronologyTableHtml,
  formatNarrativeOverview, formatNarrativeOverviewHtml,
} from '../../utils/copyFormatters';
import type { ContextItem } from '../../contexts/AskSidebarContext';
import { ChronologyQueryBar } from '../ChronologyQueryBar';
import { ChronologyQueryResults } from '../ChronologyQueryResults';
import type { ChronologyQueryResponse } from '../../services/chronologyApi';
import { useAskSidebar } from '../../contexts/AskSidebarContext';
import { useHighlightParam } from '../../hooks/useHighlightParam';
import { usePdfPreloader } from '../../hooks/usePdfPreloader';

interface DocumentTypeClassification {
  documentId: string;
  type: string;
  confidence: number;
  summary?: string;
}

interface MetadataExtractionResponse {
  patients: any[];
  keyDates: any[];
  documentTypes: DocumentTypeClassification[];
  confidence: number;
  reasoning: string;
}

/** Safely extract a date string from an event's timestamp (which may be null, a string, or {start,end}). */
function getEventDateStr(timestamp: any): string | null {
  if (!timestamp) return null;
  if (typeof timestamp === 'string') return timestamp;
  return timestamp.start ?? null;
}

// Use the correct interface from chronologyApi
interface MedicalEvent {
  eventId: string;
  type: string;
  significance?: 'MAJOR' | 'SUPPORTING' | 'ROUTINE';
  title?: string;
  summary?: string;
  clinician?: string;
  location?: string;
  description: string;
  timestamp: string | { start: string; end: string };
  certainty: string;
  extractionConfidence: number;
  validationStatus?: string;
  lastModifiedBy?: string;
  originalHeadline?: string;
  originalSummary?: string;
  originalDescription?: string;
  originalEventDate?: string;
  originalEventType?: string;
  isEdited?: boolean;
  sourceReference: {
    documentId: string;
    documentName: string;
    pageNumber: number;
    characterStart: number;
    characterEnd: number;
  };
  citations?: RAGCitation[];
}

export type SortMode = 'date-desc' | 'date-asc';

const SORT_LABELS: Record<SortMode, string> = {
  'date-desc': 'Newest',
  'date-asc': 'Oldest',
};

interface ChronologyTabProps {
  caseId: string;
}

export const ChronologyTab: React.FC<ChronologyTabProps> = ({ caseId }) => {
  const [viewMode, setViewMode] = useState<'summary' | 'graphical'>('summary');
  const [filters, setFilters] = useState<FilterOptions>({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [enabledEventTypes, setEnabledEventTypes] = useState<Set<string>>(new Set());
  const [enabledSignificance, setEnabledSignificance] = useState<Set<string>>(new Set(['MAJOR', 'SUPPORTING']));
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [showChildEvents, setShowChildEvents] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<EditedFields>({});
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [showOriginalForEventId, setShowOriginalForEventId] = useState<string | null>(null);
  const [explainEventId, setExplainEventId] = useState<string | null>(null);
  const [eventAnnotations, setEventAnnotations] = useState<Record<string, EventAnnotation[]>>({});
  const [eventSourceSummaries, setEventSourceSummaries] = useState<Record<string, string>>({});
  const [explainLoading, setExplainLoading] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>('date-desc');
  const [showCopyMenu, setShowCopyMenu] = useState(false);
  const copyMenuRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const monthRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  
  const [queryResult, setQueryResult] = useState<ChronologyQueryResponse | null>(null);
  const [showExpertEvidence, setShowExpertEvidence] = useState(false);

  const { openDocument, activeDocument } = useDocumentViewer();
  const { isEventMarked, addFinding } = useKeyFindings();
  const { copied: copiedChronology, copy: copyToClipboard } = useCopyToClipboard();
  const { submitAskMessage, addToContext } = useAskSidebar();

  // Auto-scroll to a highlighted event from Key Findings jump
  const { highlightId, scrollToHighlighted } = useHighlightParam();
  useEffect(() => {
    if (!highlightId) return;
    scrollToHighlighted();
  }, [highlightId, scrollToHighlighted]);

  const isDateSort = sortMode === 'date-desc' || sortMode === 'date-asc';

  const handleQueryResult = useCallback((result: ChronologyQueryResponse) => {
    setQueryResult(result);
    // Switch to graphical view to show results on the timeline
    if (viewMode === 'summary') setViewMode('graphical');
  }, [viewMode]);

  const clearQueryResult = useCallback(() => {
    setQueryResult(null);
  }, []);

  const handleOpenAssistant = useCallback((prefill: string) => {
    submitAskMessage(prefill);
  }, [submitAskMessage]);

  // Close copy menu on click outside
  useEffect(() => {
    if (!showCopyMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (copyMenuRef.current && !copyMenuRef.current.contains(e.target as Node)) {
        setShowCopyMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCopyMenu]);

  // Text selection state for "Mark as Key Finding" popover
  const [selectionPopover, setSelectionPopover] = useState<{
    position: { x: number; y: number };
    eventId: string;
    text: string;
    startOffset: number;
    endOffset: number;
  } | null>(null);

  const { data: timelineData, isLoading, error } = useQuery(
    ['timeline', caseId, filters],
    () => timelineApi.getTimeline(caseId, filters),
    {
      enabled: !!caseId,
      retry: 1, // Only retry once to avoid spamming console
      retryDelay: 1000,
      onError: (error) => {
        console.error('Timeline query error:', error);
      }
    }
  );

  // Preload most-cited PDFs into memory cache for instant citation navigation
  usePdfPreloader(timelineData?.timeline?.events);

  // When an external jump targets an event, force the graphical (all events)
  // view and ensure the current filters actually render it. The summary view
  // only shows a narrative subset, so jumps would otherwise miss the target.
  useEffect(() => {
    if (!highlightId) return;
    setViewMode('graphical');
    const target = (timelineData?.timeline?.events || []).find(
      (e: any) => e.eventId === highlightId
    ) as any;
    if (!target) return;
    const sig = (target.significance || 'ROUTINE').toUpperCase();
    setEnabledSignificance((prev) => (prev.has(sig) ? prev : new Set([...prev, sig])));
    // Ensure event type filter includes the target event's type
    const targetType = target.type?.toLowerCase();
    if (targetType) {
      setEnabledEventTypes((prev) => (prev.size === 0 || prev.has(targetType) ? prev : new Set([...prev, targetType])));
    }
    if (target.parentEventId || target.isChild) {
      setShowChildEvents((prev) => (prev ? prev : true));
    }
    // Show expert evidence if the target event is expert-sourced
    if (!showExpertEvidence && expertDocIds.size > 0) {
      const docIds = (target.citations || []).map((c: any) => c.documentId).filter(Boolean);
      const isExpert = docIds.length > 0 ? docIds.every((id: string) => expertDocIds.has(id)) : (target.sourceReference?.documentId ? expertDocIds.has(target.sourceReference.documentId) : false);
      if (isExpert) setShowExpertEvidence(true);
    }
  }, [highlightId, timelineData]);

  // Read metadata from React Query cache (populated by useMetadataExtraction in CaseDetailPage)
  const { data: metadata } = useQuery<MetadataExtractionResponse | null>(
    ['metadata', caseId],
    { enabled: false }
  );

  // Build set of document IDs classified as expert reports / witness statements
  const expertDocIds = useMemo(() => {
    const ids = new Set<string>();
    for (const dt of metadata?.documentTypes || []) {
      if (dt.type === 'EXPERT_REPORT' || dt.type === 'WITNESS_STATEMENT') {
        ids.add(dt.documentId);
      }
    }
    return ids;
  }, [metadata]);

  // Fall back from summary to graphical if no narrative data is available
  useEffect(() => {
    if (!isLoading && timelineData && viewMode === 'summary' && !timelineData.timeline?.narrative) {
      setViewMode('graphical');
    }
  }, [isLoading, timelineData, viewMode]);

  const handleViewDocument = (
    documentId: string,
    pageNumber?: number,
    _startPosition?: number,
    _endPosition?: number,
    searchText?: string
  ) => {
    // Find document name from events
    const events = (timelineData?.timeline?.events || []) as any[];
    let documentName = 'Document';

    for (const event of events) {
      if (event.sourceReference?.documentId === documentId) {
        documentName = event.sourceReference.documentName;
        break;
      }
      if (event.citations) {
        const citation = event.citations.find((c: any) => c.documentId === documentId);
        if (citation) {
          documentName = citation.documentName;
          break;
        }
      }
    }

    openDocument({ documentId, documentName, pageNumber, searchText });
  };


  const getEventTypeBadge = (type: string) => {
    const labels: Record<string, string> = {
      hospital_visit: 'Hospital Visit', outpatient_appointment: 'Outpatient',
      emergency_visit: 'Emergency', surgical_episode: 'Surgery',
      treatment_course: 'Treatment', admission: 'Admission',
      discharge: 'Discharge', diagnosis: 'Diagnosis',
      major_procedure: 'Procedure', medication_routine: 'Medication',
      vital_signs: 'Vitals', lab_result: 'Lab Result',
      consultation: 'Consultation', imaging_study: 'Imaging',
      observation: 'Observation', administrative: 'Administrative',
      medication: 'Medication', investigation: 'Investigation',
      procedure: 'Procedure', other: 'General',
      // Routine / primary care types
      gp_visit: 'GP Visit', gp_telephone_consultation: 'GP Phone',
      prescription_renewal: 'Prescription', immunisation: 'Immunisation',
      health_screening: 'Screening', sight_test: 'Sight Test',
      dental_appointment: 'Dental', physiotherapy: 'Physiotherapy',
      mental_health_review: 'Mental Health', allied_health: 'Allied Health',
      nursing_care: 'Nursing', referral_letter: 'Referral',
      correspondence: 'Correspondence', radiology_routine: 'Routine Imaging',
      blood_test_routine: 'Routine Bloods', sick_note: 'Sick Note',
      treatment_change: 'Treatment Change',
      clinical_deterioration: 'Deterioration', clinical_improvement: 'Improvement',
      significant_investigation: 'Investigation',
      expert_examination: 'Expert Examination',
      expert_opinion: 'Expert Opinion',
    };
    return labels[type.toLowerCase()] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getEventTypeBadgeColor = (type: string) => {
    const colors: Record<string, string> = {
      hospital_visit: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
      emergency_visit: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
      outpatient_appointment: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
      surgical_episode: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
      treatment_course: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
      admission: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
      discharge: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
      diagnosis: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
      major_procedure: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
      medication_routine: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
      medication: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
      vital_signs: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
      lab_result: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
      investigation: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
      consultation: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
      imaging_study: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
      procedure: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
      observation: 'bg-slate-100 text-slate-600 dark:bg-slate-800/40 dark:text-slate-300',
      administrative: 'bg-gray-100 text-gray-600 dark:bg-gray-800/40 dark:text-gray-300',
      // Routine / primary care types
      gp_visit: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
      gp_telephone_consultation: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
      prescription_renewal: 'bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-300',
      immunisation: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
      health_screening: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
      sight_test: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
      dental_appointment: 'bg-stone-100 text-stone-700 dark:bg-stone-900/40 dark:text-stone-300',
      physiotherapy: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
      mental_health_review: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300',
      allied_health: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
      nursing_care: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
      referral_letter: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
      correspondence: 'bg-slate-100 text-slate-600 dark:bg-slate-800/40 dark:text-slate-300',
      radiology_routine: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
      blood_test_routine: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
      sick_note: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
      treatment_change: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
      clinical_deterioration: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
      clinical_improvement: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
      significant_investigation: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
      expert_examination: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
      expert_opinion: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
    };
    return colors[type.toLowerCase()] || 'bg-gray-100 text-gray-600 dark:bg-gray-800/40 dark:text-gray-300';
  };

  /** Strip raw type prefixes like "IMAGING_STUDY:" from descriptions.
   *  Citation marker stripping is handled by MarkdownWithCitations. */
  const stripTypePrefix = (description: string, type: string) => {
    if (!description) return 'No description available';
    let text = description;
    const typePrefix = type.toUpperCase().replace(/ /g, '_');
    if (text.startsWith(typePrefix + ':')) {
      text = text.slice(typePrefix.length + 1).trim();
    }
    const label = getEventTypeBadge(type).toUpperCase();
    if (text.toUpperCase().startsWith(label + ':')) {
      text = text.slice(label.length + 1).trim();
    }
    return text;
  };

  // Categorize child events into meaningful groups
  const categorizeChildEvent = (event: any): { category: string; icon: string; summary: string } => {
    const desc = (event.description || '').toLowerCase();
    const type = (event.type || '').toLowerCase();
    
    // Medications
    if (desc.includes('prescribed') || desc.includes('medication') || 
        desc.includes('mg') || desc.includes('tablet') || desc.includes('spray')) {
      const match = event.description.match(/^([^:]+):/);
      const drugName = match ? match[1].trim() : event.description.substring(0, 50);
      return { category: 'Medication', icon: '💊', summary: drugName };
    }
    
    // Vital signs / Observations
    if (type === 'vital_signs' || desc.includes('vital signs') || 
        desc.includes('blood pressure') || desc.includes('heart rate') ||
        desc.includes('temperature') || desc.includes('oxygen')) {
      const time = event.description.match(/(\d{4})\s*hours?/)?.[1] || '';
      return { category: 'Observation', icon: '❤️', summary: time ? `Vitals at ${time}` : 'Vital signs recorded' };
    }
    
    // Nursing notes / Care
    if (desc.includes('nursing') || desc.includes('ambulation') || 
        desc.includes('intake') || desc.includes('output') || desc.includes('personal care')) {
      const activity = event.description.match(/^([^:]+):/)?.[1] || 'Nursing care';
      return { category: 'Care', icon: '👨‍⚕️', summary: activity.trim() };
    }
    
    // Clinical notes
    if (desc.includes('handwritten') || desc.includes('clinical note') || desc.includes('assessment')) {
      return { category: 'Note', icon: '📝', summary: 'Clinical assessment note' };
    }
    
    // Diagnoses
    if (type === 'diagnosis' || desc.includes('diagnosed')) {
      const match = event.description.match(/^([^:]+):/);
      const diagnosis = match ? match[1].trim() : event.description.substring(0, 50);
      return { category: 'Diagnosis', icon: '🩺', summary: diagnosis };
    }
    
    // Default
    return { category: 'Detail', icon: '📄', summary: event.description.substring(0, 60) + '...' };
  };

  const renderChildEvent = (event: any, index: number) => {
    const { category, icon } = categorizeChildEvent(event);

    return (
      <div className="relative pl-8 group">
        {/* Small icon */}
        <div className="absolute left-0 flex items-center justify-center w-5 h-5">
          <span className="text-sm">{icon}</span>
        </div>
        
        {/* Compact detail card */}
        <div className="bg-white dark:bg-medical-800 border border-medical-200 dark:border-medical-700 rounded-md p-3 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-sm transition-all">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-primary-600 dark:text-primary-500 uppercase tracking-wide">
                  {category}
                </span>
              </div>
              <div className="text-sm text-medical-800 dark:text-medical-200 leading-snug prose prose-sm dark:prose-invert max-w-none">
                <MarkdownWithCitations
                  content={formatClinicalDescription(stripTypePrefix(event.description, event.type || ''))}
                  citations={event.citations?.map((c: RAGCitation) => fromRAGCitation(c)) || []}
                />
              </div>
            </div>

            {/* View details button */}
            <button
              onClick={() => {
                if (event.sourceReference?.documentId) {
                  // Pick the best citation excerpt for highlighting
                  const citationText = event.citations?.find(
                    (c: any) => c.documentId === event.sourceReference?.documentId
                  )?.sourceText;
                  handleViewDocument(
                    event.sourceReference.documentId,
                    event.sourceReference.pageNumber,
                    event.sourceReference.characterStart,
                    event.sourceReference.characterEnd,
                    citationText
                  );
                }
              }}
              className="flex-shrink-0 text-xs text-primary-600 dark:text-primary-500 hover:text-primary-800 dark:hover:text-primary-300 font-medium"
            >
              Details
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderEvent = (event: any, index: number, isParent: boolean) => {
    try {
      const hasCitations = event.citations && event.citations.length > 0;
      const eventType = event.type || 'unknown';
      const isParentType = isParent || ['hospital_visit', 'outpatient_appointment', 'emergency_visit', 'surgical_episode', 'treatment_course'].includes(eventType.toLowerCase());
      const confidence = Math.round((event.extractionConfidence || 0) * 100);
      const eventIsMarked = isEventMarked(event.eventId);

      return (
        <div className="relative pl-12 group" data-event-id={event.eventId}>
          {/* Timeline dot */}
          <div className={`absolute left-0 flex items-center justify-center ${
            isParentType ? 'w-[42px] h-[42px]' : 'w-[42px] h-[42px]'
          }`}>
            <div className={`rounded-full transition-all duration-150 ${
              isParentType
                ? 'w-3 h-3 bg-primary-500 ring-4 ring-primary-100 dark:ring-primary-900/50'
                : 'w-2.5 h-2.5 bg-medical-300 dark:bg-medical-500 ring-4 ring-medical-100 dark:ring-medical-800'
            }`} />
          </div>

          {/* Event card */}
          <div
            style={explainLoading === event.eventId ? { animation: 'border-pulse 1.5s ease-in-out infinite' } : undefined}
            className={`rounded-lg border bg-white dark:bg-medical-800 transition-all duration-150 hover:shadow-md ${
            explainLoading === event.eventId
              ? (isParentType ? 'p-4' : 'p-3')
              : explainEventId === event.eventId
                ? 'border-emerald-300 dark:border-emerald-700 shadow-sm ' + (isParentType ? 'p-4' : 'p-3')
                : eventIsMarked
                  ? 'border-amber-300 dark:border-amber-600 shadow-sm ' + (isParentType ? 'p-4' : 'p-3')
                  : isParentType
                    ? 'border-medical-200 dark:border-medical-700 shadow-sm p-4'
                    : 'border-medical-200 dark:border-medical-700 p-3'
          }`}>
            {/* Event header */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {/* Entry actions — hover-revealed */}
              <EntryActions
                compact
                item={{
                  id: event.eventId || `event-${index}`,
                  type: 'event' as ContextItem['type'],
                  summary: event.title || stripTypePrefix(event.description, eventType).replace(/\[\d+\]/g, '').slice(0, 80),
                  source: `Chronology — ${getEventTypeBadge(eventType)}`,
                  data: { eventId: event.eventId, type: eventType, timestamp: event.timestamp, description: event.description },
                }}
                className="mr-1"
                onCopy={() => ({ text: formatChronologyEvent(event), html: formatChronologyEventHtml(event) })}
                onExplain={() => toggleExplain(event.eventId)}
                isExplaining={explainEventId === event.eventId}
                isExplainLoading={explainLoading === event.eventId}
              />
              {/* Citation excerpt debug popover */}
              {event.citations && event.citations.length > 0 && (
                <span className="relative group/cite">
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center justify-center w-6 h-6 rounded-md text-medical-400 dark:text-medical-500 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/30 transition-colors"
                    title="View citation excerpts"
                  >
                    <Quote className="w-3.5 h-3.5" />
                  </button>
                  <div className="absolute left-0 top-full mt-1 w-[420px] max-h-80 overflow-y-auto rounded-lg bg-white dark:bg-medical-800 border border-medical-200 dark:border-medical-600 shadow-xl opacity-0 invisible group-hover/cite:opacity-100 group-hover/cite:visible transition-all duration-150 z-50 p-3 space-y-2.5">
                    <div className="text-[11px] font-semibold text-medical-500 dark:text-medical-400 uppercase tracking-wide mb-1">
                      Citation excerpts ({event.citations.length})
                    </div>
                    {event.citations.map((c: RAGCitation, ci: number) => (
                      <div key={ci} className="rounded border border-medical-100 dark:border-medical-700 bg-medical-50/50 dark:bg-medical-700/30 p-2.5">
                        <div className="flex items-center gap-2 mb-1.5 text-[10px] text-medical-500 dark:text-medical-400">
                          <span className="font-medium">[{ci + 1}]</span>
                          <span className="truncate">{c.documentName}</span>
                          {c.pageNumber != null && <span className="shrink-0">p.{c.pageNumber}</span>}
                        </div>
                        <p className="text-xs text-medical-700 dark:text-medical-300 leading-relaxed whitespace-pre-wrap break-words font-mono">
                          {c.sourceText || c.contextText || '(no excerpt)'}
                        </p>
                      </div>
                    ))}
                  </div>
                </span>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (editingEventId === event.eventId) {
                    setEditingEventId(null);
                    setEditValues({});
                  } else {
                    setEditingEventId(event.eventId);
                    setEditValues({
                      headline: parsePiiMarkers(event.title || '').cleanText.replace(/\s*\[\d+\]/g, ''),
                      summary: parsePiiMarkers(event.summary || '').cleanText.replace(/\s*\[\d+\]/g, ''),
                      description: parsePiiMarkers(event.description || '').cleanText.replace(/\s*\[\d+\]/g, ''),
                      eventDate: getEventDateStr(event.timestamp)?.slice(0, 16) || '',
                      eventType: eventType,
                    });
                  }
                }}
                className="inline-flex items-center justify-center w-6 h-6 rounded-md text-medical-400 dark:text-medical-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
                title="Edit event"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              {editingEventId === event.eventId ? (
                <select
                  value={editValues.eventType || eventType}
                  onChange={(e) => { e.stopPropagation(); setEditValues(v => ({ ...v, eventType: e.target.value })); }}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white dark:bg-medical-700 border border-primary-300 dark:border-primary-600 text-medical-900 dark:text-medical-100 outline-none cursor-pointer"
                >
                  {EVENT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getEventTypeBadgeColor(eventType)}`}>
                  {getEventTypeBadge(eventType)}
                </span>
              )}
              {showExpertEvidence && isExpertSourced(event) && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                  <Scale className="w-3 h-3" />
                  Expert Source
                </span>
              )}
              {(event.validationStatus === 'VALIDATED' || event.isEdited) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowOriginalForEventId(
                      showOriginalForEventId === event.eventId ? null : event.eventId
                    );
                  }}
                  className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800/60 cursor-pointer transition-colors"
                  title="Click to view original content"
                >
                  Edited
                </button>
              )}
              <span className="text-medical-300 dark:text-medical-600">·</span>
              {editingEventId === event.eventId ? (
                <input
                  type="datetime-local"
                  value={editValues.eventDate || ''}
                  onChange={(e) => { e.stopPropagation(); setEditValues(v => ({ ...v, eventDate: e.target.value })); }}
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs tabular-nums bg-white dark:bg-medical-700 border border-primary-300 dark:border-primary-600 text-medical-900 dark:text-medical-100 rounded px-1.5 py-0.5 outline-none"
                />
              ) : (
                <time className="text-xs text-medical-500 dark:text-medical-400 tabular-nums">
                  {formatEventDate(event.timestamp || new Date().toISOString())}
                </time>
              )}
              {confidence > 0 && confidence < 80 && (
                <>
                  <span className="text-medical-300 dark:text-medical-600">·</span>
                  <span className="relative group/conf">
                    <span className="inline-flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400 font-medium cursor-help underline decoration-dashed decoration-yellow-400/50 dark:decoration-yellow-500/50 underline-offset-2 hover:text-yellow-700 dark:hover:text-yellow-300 transition-colors">
                      {confidence}% confidence
                      <Info className="w-3 h-3" />
                    </span>
                    {/* Invisible bridge so the mouse can travel from trigger to popover */}
                    <span className="absolute left-0 right-0 bottom-full h-3 hidden group-hover/conf:block" />
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 w-72 px-3 py-2.5 rounded-lg bg-white dark:bg-medical-800 border border-medical-200 dark:border-medical-600 text-medical-700 dark:text-medical-200 text-xs leading-relaxed shadow-lg opacity-0 invisible group-hover/conf:opacity-100 group-hover/conf:visible transition-all duration-150 z-50">
                      <span className="font-semibold text-medical-900 dark:text-medical-100 block mb-1">Why is confidence lower here?</span>
                      Confidence reflects how clearly the source records support this finding. Abbreviated notes, ambiguous dates, or handwritten entries often reduce the score. This does not mean the finding is incorrect — only that the original evidence is less explicit.
                      <button
                        className="mt-2 block text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          addToContext({
                            id: event.eventId || `event-${index}`,
                            type: 'event',
                            summary: event.title || stripTypePrefix(event.description, eventType).replace(/\[\d+\]/g, '').slice(0, 80),
                            source: `Chronology — ${getEventTypeBadge(eventType)}`,
                            data: { eventId: event.eventId, type: eventType, timestamp: event.timestamp, description: event.description, confidence },
                          });
                          submitAskMessage(`Why does the event "${event.title || event.description?.slice(0, 60)}" on ${formatEventDate(event.timestamp || '')} have a confidence score of ${confidence}%? What in the source records makes it uncertain?`);
                        }}
                      >
                        Ask ClinLexis to explain &rarr;
                      </button>
                      <span className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-white dark:border-t-medical-800" />
                    </span>
                  </span>
                </>
              )}
            </div>

            {/* Event title (headline) */}
            {editingEventId === event.eventId ? (
              <input
                type="text"
                value={editValues.headline || ''}
                onChange={(e) => setEditValues(v => ({ ...v, headline: e.target.value }))}
                onClick={(e) => e.stopPropagation()}
                placeholder="Headline"
                maxLength={200}
                className="w-full text-sm font-semibold text-medical-900 dark:text-medical-100 mb-1 bg-white dark:bg-medical-700 border border-primary-300 dark:border-primary-600 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-primary-400"
              />
            ) : event.title && event.title !== event.description ? (
              <h4 className="text-sm font-semibold text-medical-900 dark:text-medical-100 mb-1">
                {explainEventId === event.eventId && eventAnnotations[event.eventId] ? (
                  <AnnotatedEventText
                    text={event.title}
                    annotations={eventAnnotations[event.eventId]}
                  />
                ) : (
                  <RedactionText text={event.title} />
                )}
              </h4>
            ) : null}

            {/* Clinician and location byline */}
            {(event.clinician || event.location) && (
              <div className="flex items-center gap-1.5 mb-1.5 text-xs text-medical-500 dark:text-medical-400">
                {event.clinician && (
                  <span className="font-medium text-medical-700 dark:text-medical-300"><RedactionText text={event.clinician} /></span>
                )}
                {event.clinician && event.location && (
                  <span className="text-medical-300 dark:text-medical-600">&mdash;</span>
                )}
                {event.location && (
                  <span><RedactionText text={event.location} /></span>
                )}
              </div>
            )}


            {/* Event summary / description with expand-collapse */}
            {editingEventId === event.eventId ? (
              <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                <textarea
                  value={editValues.description || ''}
                  onChange={(e) => setEditValues(v => ({ ...v, description: e.target.value }))}
                  maxLength={2000}
                  rows={4}
                  className="w-full text-sm text-medical-900 dark:text-medical-100 leading-relaxed bg-white dark:bg-medical-700 border border-primary-300 dark:border-primary-600 rounded px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-primary-400 resize-y"
                />
              </div>
            ) : (
              <div
                className="text-sm text-medical-700 dark:text-medical-300 leading-relaxed prose prose-sm dark:prose-invert max-w-none"
                onMouseUp={(e) => { if (explainEventId !== event.eventId) handleDescriptionMouseUp(e, event.eventId); }}
              >
                {explainEventId === event.eventId && eventAnnotations[event.eventId] ? (
                  <AnnotatedEventText
                    text={formatClinicalDescription(stripTypePrefix(
                      expandedEvents.has(event.eventId) ? (event.summary || event.description) : event.description,
                      eventType
                    ))}
                    annotations={eventAnnotations[event.eventId]}
                  />
                ) : (
                  <MarkdownWithCitations
                    content={formatClinicalDescription(stripTypePrefix(
                      expandedEvents.has(event.eventId) ? (event.summary || event.description) : event.description,
                      eventType
                    ))}
                    citations={event.citations?.map((c: RAGCitation) => fromRAGCitation(c)) || []}
                  />
                )}
              </div>
            )}

            {/* Annotation legend + provenance summary */}
            {explainEventId === event.eventId && eventAnnotations[event.eventId]?.length > 0 && (
              <div className="mt-3 pt-2.5 border-t border-medical-100 dark:border-medical-700/50 space-y-2">
                {/* Legend */}
                <div className="flex items-center gap-3 text-[11px] text-medical-500 dark:text-medical-400">
                  <span className="flex items-center gap-1.5">
                    <span className="w-5 border-b-2 border-emerald-400 dark:border-emerald-500 border-solid" />
                    <span className="font-medium">Verbatim</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-5 border-b-2 border-amber-400 dark:border-amber-500 border-dashed" />
                    <span className="font-medium">Summarised</span>
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-5 border-b-2 border-indigo-400 dark:border-indigo-500 border-dotted" />
                    <span className="font-medium">Synthesised</span>
                  </span>
                  <span className="ml-auto text-[10px] italic text-medical-400 dark:text-medical-500">Hover for details · Click to view source</span>
                </div>

                {/* Provenance prose summary */}
                {eventSourceSummaries[event.eventId] && (
                  <div className="rounded-lg bg-medical-50 dark:bg-medical-700/30 border border-medical-100 dark:border-medical-700/50 px-3.5 py-2.5">
                    <p className="text-xs leading-relaxed text-medical-600 dark:text-medical-400">
                      {eventSourceSummaries[event.eventId]}
                    </p>
                    <button
                      className="mt-2 text-[11px] font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        submitAskMessage(`Tell me more about the event "${event.title || event.description?.slice(0, 60)}" on ${formatEventDate(event.timestamp || '')}. What do the source records say and what is clinically significant?`);
                      }}
                    >
                      Dive deeper with ClinLexis →
                    </button>
                  </div>
                )}
              </div>
            )}



            {/* Save / Cancel bar when editing */}
            {editingEventId === event.eventId && (
              <div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => {
                    // Build only changed fields
                    const changes: EditedFields = {};
                    if (editValues.headline !== (event.title || '')) changes.headline = editValues.headline;
                    if (editValues.description !== (event.description || '')) changes.description = editValues.description;
                    if (editValues.eventDate !== (getEventDateStr(event.timestamp)?.slice(0, 16) || '')) changes.eventDate = editValues.eventDate;
                    if (editValues.eventType !== eventType) changes.eventType = editValues.eventType;
                    if (Object.keys(changes).length === 0) {
                      setEditingEventId(null);
                      setEditValues({});
                      return;
                    }
                    setEditValues(changes);
                    setShowCorrectionModal(true);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={() => { setEditingEventId(null); setEditValues({}); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-medical-600 dark:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Correction reason modal */}
            {editingEventId === event.eventId && showCorrectionModal && (
              <CorrectionReasonModal
                eventId={event.eventId}
                caseId={caseId}
                changes={editValues}
                onClose={() => setShowCorrectionModal(false)}
                onSaved={() => {
                  setShowCorrectionModal(false);
                  setEditingEventId(null);
                  setEditValues({});
                }}
              />
            )}

            {/* Original content popover */}
            {showOriginalForEventId === event.eventId && (
              <OriginalContentPopover
                event={event}
                caseId={caseId}
                onClose={() => setShowOriginalForEventId(null)}
                onReverted={() => setShowOriginalForEventId(null)}
              />
            )}

          </div>
        </div>
      );
    } catch (eventError) {
      console.error('Error rendering event:', eventError);
      return (
        <div className="relative pl-14">
          <div className="card bg-red-50 border-red-200">
            <p className="text-red-600 text-sm">Error rendering event {index + 1}</p>
          </div>
        </div>
      );
    }
  };

  // Handle text selection within event descriptions
  const handleDescriptionMouseUp = (e: React.MouseEvent, eventId: string) => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !selection.toString().trim()) {
      return;
    }
    const selectedText = selection.toString().trim();
    if (selectedText.length < 3) return; // Ignore tiny selections

    // Find the offset within the description element
    const range = selection.getRangeAt(0);
    const descEl = (e.currentTarget as HTMLElement);
    const preRange = document.createRange();
    preRange.setStart(descEl, 0);
    preRange.setEnd(range.startContainer, range.startOffset);
    const startOffset = preRange.toString().length;
    const endOffset = startOffset + selectedText.length;

    // Position the popover near the selection
    const rect = range.getBoundingClientRect();
    setSelectionPopover({
      position: { x: rect.left + rect.width / 2 - 60, y: rect.bottom + 4 },
      eventId,
      text: selectedText,
      startOffset,
      endOffset,
    });
  };

  const formatEventDate = (timestamp: string | { start: string; end: string } | null | undefined) => {
    if (!timestamp) return 'Unknown date';
    if (typeof timestamp === 'string') {
      return formatDate(timestamp);
    } else {
      const start = formatDate(timestamp.start);
      const end = formatDate(timestamp.end);
      return start === end ? start : `${start} - ${end}`;
    }
  };

  const timeline = timelineData?.timeline;
  const events = timeline?.events || [];
  const gaps = timeline?.gaps || [];
  const conflicts = timeline?.conflicts || [];

  // Group events by month
  const monthGroups = React.useMemo(() => {
    const groups = new Map<string, any[]>();
    const MONTH_NAMES = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    events.forEach((event: any) => {
      const raw = getEventDateStr(event.timestamp);
      if (!raw) return;
      const date = new Date(raw);
      if (isNaN(date.getTime())) return;
      const year = date.getFullYear();
      const month = date.getMonth();
      const key = `${year}-${month}`;

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(event);
    });
    
    return Array.from(groups.entries())
      .map(([key, events]) => {
        const [year, month] = key.split('-').map(Number);
        return {
          year,
          month,
          key,
          label: `${MONTH_NAMES[month]} ${year}`,
          count: events.length,
          events
        };
      })
      .sort((a, b) => {
        const dir = sortMode === 'date-asc' ? 1 : -1;
        if (a.year !== b.year) return dir * (a.year - b.year);
        return dir * (a.month - b.month);
      });
  }, [events, sortMode]);

  // Event type filters
  // Broad filter groups — each maps a user-facing label to the raw event types it includes
  const EVENT_TYPE_GROUPS: { label: string; types: string[] }[] = [
    { label: 'Encounters', types: ['hospital_visit', 'outpatient_appointment', 'emergency_visit', 'admission', 'discharge', 'gp_visit', 'gp_telephone_consultation', 'consultation'] },
    { label: 'Diagnoses', types: ['diagnosis'] },
    { label: 'Investigations & Results', types: ['significant_investigation', 'investigation', 'lab_result', 'radiology_routine', 'blood_test_routine', 'health_screening', 'sight_test'] },
    { label: 'Treatment & Medication', types: ['procedure', 'major_procedure', 'surgical_episode', 'medication', 'medication_routine', 'treatment_change', 'prescription_renewal'] },
    { label: 'Referrals & Correspondence', types: ['referral_letter', 'correspondence', 'sick_note', 'administrative'] },
    { label: 'Allied & Community Health', types: ['physiotherapy', 'allied_health', 'mental_health_review', 'nursing_care', 'dental_appointment', 'immunisation'] },
    { label: 'Expert Examinations', types: ['expert_examination'] },
  ];

  const eventTypeFilters = React.useMemo(() => {
    const typeCounts = new Map<string, number>();
    events.forEach((event: any) => {
      const type = event.type?.toLowerCase() || 'unknown';
      typeCounts.set(type, (typeCounts.get(type) || 0) + 1);
    });

    const allGroupedTypes = new Set(EVENT_TYPE_GROUPS.flatMap(g => g.types));

    const results = EVENT_TYPE_GROUPS
      .map(group => {
        const count = group.types.reduce((sum, t) => sum + (typeCounts.get(t) || 0), 0);
        const presentTypes = group.types.filter(t => typeCounts.has(t));
        const enabled = enabledEventTypes.size === 0 || presentTypes.every(t => enabledEventTypes.has(t));
        return { type: group.types[0], label: group.label, types: group.types, count, enabled };
      })
      .filter(g => g.count > 0);

    // Catch-all for types not in any defined group
    const ungroupedTypes = [...typeCounts.keys()].filter(t => !allGroupedTypes.has(t));
    if (ungroupedTypes.length > 0) {
      const count = ungroupedTypes.reduce((sum, t) => sum + (typeCounts.get(t) || 0), 0);
      const enabled = enabledEventTypes.size === 0 || ungroupedTypes.every(t => enabledEventTypes.has(t));
      results.push({ type: ungroupedTypes[0], label: 'Other', types: ungroupedTypes, count, enabled });
    }

    return results;
  }, [events, enabledEventTypes]);

  // Significance level counts for filter badges
  const significanceCounts = React.useMemo(() => {
    const counts = { MAJOR: 0, SUPPORTING: 0, ROUTINE: 0 };
    events.forEach((e: any) => {
      const sig = (e.significance || 'MAJOR') as keyof typeof counts;
      if (sig in counts) counts[sig]++;
    });
    return counts;
  }, [events]);

  // Initialize enabled types
  useEffect(() => {
    if (enabledEventTypes.size === 0 && events.length > 0) {
      const types = new Set(events.map((e: any) => e.type?.toLowerCase()).filter(Boolean));
      setEnabledEventTypes(types);
    }
  }, [events, enabledEventTypes.size]);

  // Check whether an event is sourced from an expert document
  const isExpertSourced = useCallback((event: any): boolean => {
    if (expertDocIds.size === 0) return false;
    const docIds = (event.citations || []).map((c: any) => c.documentId).filter(Boolean);
    if (docIds.length === 0) {
      // Fall back to sourceReference if no citations
      return event.sourceReference?.documentId ? expertDocIds.has(event.sourceReference.documentId) : false;
    }
    return docIds.every((id: string) => expertDocIds.has(id));
  }, [expertDocIds]);

  // Count of expert-sourced events (for toggle badge)
  const expertEventCount = useMemo(() => {
    return events.filter(isExpertSourced).length;
  }, [events, isExpertSourced]);

  // Filter events by enabled types and date range
  const filteredEvents = React.useMemo(() => {
    let filtered = events;

    // Filter by event type
    if (enabledEventTypes.size > 0) {
      filtered = filtered.filter((event: any) =>
        enabledEventTypes.has(event.type?.toLowerCase())
      );
    }

    // Filter by significance level (expert-sourced events bypass when Expert toggle is on)
    if (enabledSignificance.size < 3) {
      filtered = filtered.filter((event: any) => {
        if (showExpertEvidence && isExpertSourced(event)) return true;
        const sig = event.significance || 'MAJOR';
        return enabledSignificance.has(sig);
      });
    }

    // Filter expert-sourced events (hidden by default)
    if (!showExpertEvidence && expertDocIds.size > 0) {
      filtered = filtered.filter(event => !isExpertSourced(event));
    }

    return filtered;
  }, [events, enabledEventTypes, enabledSignificance, showExpertEvidence, expertDocIds, isExpertSourced]);

  // Sort filtered events based on selected sort mode
  const sortedFilteredEvents = React.useMemo(() => {
    const sorted = [...filteredEvents];
    switch (sortMode) {
      case 'date-asc':
        sorted.sort((a: any, b: any) => {
          const ta = new Date(getEventDateStr(a.timestamp) || 0).getTime();
          const tb = new Date(getEventDateStr(b.timestamp) || 0).getTime();
          return ta - tb;
        });
        break;
      case 'date-desc':
        sorted.sort((a: any, b: any) => {
          const ta = new Date(getEventDateStr(a.timestamp) || 0).getTime();
          const tb = new Date(getEventDateStr(b.timestamp) || 0).getTime();
          return tb - ta;
        });
        break;
    }
    return sorted;
  }, [filteredEvents, sortMode]);

  // All events sorted (ignoring filters) — used for "Copy all events"
  const sortedAllEvents = React.useMemo(() => {
    const sorted = [...events];
    const dir = sortMode === 'date-asc' ? 1 : -1;
    sorted.sort((a: any, b: any) => {
      const ta = new Date(getEventDateStr(a.timestamp) || 0).getTime();
      const tb = new Date(getEventDateStr(b.timestamp) || 0).getTime();
      return dir * (ta - tb);
    });
    return sorted;
  }, [events, sortMode]);

  // Apply NL query result on top of sorted/filtered events
  // Apply NL filter/sort result on top of sorted/filtered events
  const queryFilteredEvents = useMemo(() => {
    if (!queryResult) {
      return sortedFilteredEvents;
    }
    // If no eventIds returned, that means zero matches
    if (!queryResult.eventIds || queryResult.eventIds.length === 0) {
      return [];
    }
    // Reorder/filter events to match the LLM's returned IDs
    const eventMap = new Map(sortedFilteredEvents.map((e: any) => [e.eventId, e]));
    const ordered: any[] = [];
    for (const id of queryResult.eventIds) {
      const event = eventMap.get(id);
      if (event) ordered.push(event);
    }
    return ordered;
  }, [sortedFilteredEvents, queryResult]);

  // Scroll handling
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setShowScrollTop(container.scrollTop > 500);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-collapse Quick Jump sidebar when the document panel opens
  useEffect(() => {
    if (activeDocument) {
      setSidebarCollapsed(true);
    }
  }, [activeDocument]);

  // Navigation functions
  const scrollToMonth = (year: number, month: number) => {
    const key = `${year}-${month}`;
    const element = monthRefs.current.get(key);
    if (element && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const elementTop = element.offsetTop;
      const offset = 80; // Account for sticky header
      container.scrollTo({ top: elementTop - offset, behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (type: string, enabled: boolean) => {
    // Find the group this type belongs to and toggle all its members
    const group = EVENT_TYPE_GROUPS.find(g => g.types.includes(type))
      || eventTypeFilters.find(g => g.types?.includes(type));
    const typesToToggle = group ? group.types : [type];
    const newTypes = new Set(enabledEventTypes);
    typesToToggle.forEach(t => enabled ? newTypes.add(t) : newTypes.delete(t));
    setEnabledEventTypes(newTypes);
  };

  const toggleExplain = useCallback(async (eventId: string) => {
    if (explainEventId === eventId) {
      setExplainEventId(null);
      return;
    }
    setExplainEventId(eventId);

    // If we already have annotations cached, don't re-fetch
    if (eventAnnotations[eventId]) return;

    setExplainLoading(eventId);
    try {
      const response = await chronologyApi.explainEvent(eventId);
      setEventAnnotations(prev => ({ ...prev, [eventId]: response.annotations || [] }));
      if (response.sourceSummary) {
        setEventSourceSummaries(prev => ({ ...prev, [eventId]: response.sourceSummary! }));
      }
    } catch (err) {
      console.error('Failed to fetch annotations:', err);
      setEventAnnotations(prev => ({ ...prev, [eventId]: [] }));
    } finally {
      setExplainLoading(null);
    }
  }, [explainEventId, eventAnnotations]);

  const toggleEventExpansion = (eventId: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  // Group events by parent-child relationships
  const groupedEvents = React.useMemo(() => {
    // Use query-filtered events when a query is active
    const eventsToGroup = queryResult ? queryFilteredEvents : sortedFilteredEvents;

    // Non-date sorts or active query with SORT/FILTER intent: flat list
    if (!isDateSort || (queryResult && (queryResult.intent === 'SORT' || queryResult.intent === 'FILTER'))) {
      return eventsToGroup.map((event: any) => ({ ...event, children: [] }));
    }

    // Group events by date first
    const dateGroups = new Map<string, any[]>();

    eventsToGroup.forEach((event: any) => {
      const raw = getEventDateStr(event.timestamp);
      if (!raw) return;
      const eventDate = new Date(raw);
      if (isNaN(eventDate.getTime())) return;
      const dateKey = eventDate.toISOString().split('T')[0]; // YYYY-MM-DD

      if (!dateGroups.has(dateKey)) {
        dateGroups.set(dateKey, []);
      }
      dateGroups.get(dateKey)!.push(event);
    });
    
    const result: any[] = [];
    
    // For each date, identify parent events and group related events
    dateGroups.forEach((eventsOnDate, dateKey) => {
      if (eventsOnDate.length === 1) {
        // Single event - no grouping needed
        result.push({ ...eventsOnDate[0], children: [] });
        return;
      }
      
      // Multiple events on same date - look for parent event
      // Priority: Emergency admission > Hospital visit > Diagnosis > Other
      const priorityMap: Record<string, number> = {
        'emergency_visit': 1,
        'hospital_visit': 2,
        'outpatient_appointment': 3,
        'surgical_episode': 4,
        'admission': 5,
        'diagnosis': 6
      };
      
      // Find the most significant event as parent
      let parentEvent = eventsOnDate[0];
      let lowestPriority = 999;
      
      eventsOnDate.forEach(event => {
        const eventType = (event.type || '').toLowerCase();
        const priority = priorityMap[eventType] || 999;
        
        // Also check description for keywords
        const desc = (event.description || '').toLowerCase();
        const isAdmission = desc.includes('admission') || desc.includes('admitted') || desc.includes('emergency');
        const isDiagnosis = desc.includes('diagnosed') || eventType === 'diagnosis';
        
        let effectivePriority = priority;
        if (isAdmission && priority > 5) effectivePriority = 5;
        if (isDiagnosis && priority > 6) effectivePriority = 6;
        
        if (effectivePriority < lowestPriority) {
          parentEvent = event;
          lowestPriority = effectivePriority;
        }
      });
      
      // Group remaining events as children
      const children = eventsOnDate.filter(e => e.eventId !== parentEvent.eventId);
      
      result.push({
        ...parentEvent,
        children: children
      });
    });
    
    return result;
  }, [sortedFilteredEvents, queryFilteredEvents, queryResult, isDateSort]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <Clock className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-500" />
          <span className="ml-2 text-medical-600 dark:text-medical-400">Loading timeline...</span>
        </div>
      </div>
    );
  }

  if (error && !timelineData) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="text-center">
            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-600">Error loading timeline</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Sidebar — hidden on summary view */}
      {viewMode !== 'summary' && (
        <ChronologySidebar
          monthGroups={monthGroups.map(g => ({
            year: g.year,
            month: g.month,
            count: g.count,
            label: g.label
          }))}
          eventTypeFilters={eventTypeFilters}
          onJumpToMonth={scrollToMonth}
          onFilterChange={handleFilterChange}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          isDateSort={isDateSort}
          sortLabel={SORT_LABELS[sortMode]}
          significanceLevels={[
            { key: 'MAJOR', label: 'Key Events', count: significanceCounts.MAJOR, enabled: enabledSignificance.has('MAJOR') },
            { key: 'SUPPORTING', label: 'Supporting', count: significanceCounts.SUPPORTING, enabled: enabledSignificance.has('SUPPORTING') },
            { key: 'ROUTINE', label: 'Routine', count: significanceCounts.ROUTINE, enabled: enabledSignificance.has('ROUTINE') },
          ]}
          onSignificanceChange={(key, enabled) => {
            const next = new Set(enabledSignificance);
            if (enabled) { next.add(key); } else { next.delete(key); }
            setEnabledSignificance(next);
          }}
        />
      )}

      {/* Main Content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto relative">
        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600 text-white p-3 rounded-full shadow-lg transition-all z-30"
            title="Scroll to top"
          >
            <ArrowUp className="h-5 w-5" />
          </button>
        )}

        <div className="p-6">
          {/* Row A: Query bar + Copy */}
          <div className="flex items-center gap-2 mb-3">
            {timelineData?.chronologyId && events.length > 0 ? (
              <div className="flex-1 min-w-0">
                <ChronologyQueryBar
                  chronologyId={timelineData.chronologyId}
                  onQueryResult={handleQueryResult}
                  onOpenAssistant={handleOpenAssistant}
                  disabled={events.length === 0}
                  eventCount={events.length}
                  filteredEventCount={filteredEvents.length}
                />
              </div>
            ) : (
              <div className="flex-1" />
            )}
            {/* Copy dropdown */}
            <div className="relative flex-shrink-0" ref={copyMenuRef}>
              <button
                onClick={() => setShowCopyMenu(!showCopyMenu)}
                title="Copy chronology"
                className="flex items-center gap-1 p-2 rounded-lg text-sm font-medium bg-medical-100 hover:bg-medical-200 text-medical-600 dark:bg-medical-800 dark:hover:bg-medical-700 dark:text-medical-300 transition-colors"
              >
                {copiedChronology ? <Check className="h-4 w-4 text-green-600 dark:text-green-400" /> : <Copy className="h-4 w-4" />}
                <ChevronDown className="h-3 w-3" />
              </button>
              {showCopyMenu && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-medical-800 border border-medical-200 dark:border-medical-700 rounded-lg shadow-lg z-30 py-1">
                  {/* Narrative overview */}
                  {timelineData?.timeline?.narrative && (
                    <>
                      <button
                        onClick={() => {
                          const narrative = timelineData.timeline.narrative;
                          copyToClipboard(
                            formatNarrativeOverview(narrative.overviewSummary, narrative.sections),
                            formatNarrativeOverviewHtml(narrative.overviewSummary, narrative.sections),
                          );
                          setShowCopyMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-medical-700 dark:text-medical-300 hover:bg-medical-50 dark:hover:bg-medical-700 transition-colors"
                      >
                        <span className="font-medium">Narrative overview</span>
                        <p className="text-xs text-medical-500 dark:text-medical-400 mt-0.5">Case overview and section summaries</p>
                      </button>
                      <div className="border-t border-medical-100 dark:border-medical-700 my-1" />
                    </>
                  )}
                  {/* Shown events — summaries */}
                  <button
                    onClick={() => {
                      copyToClipboard(
                        formatChronologySummaries(sortedFilteredEvents),
                        formatChronologySummariesHtml(sortedFilteredEvents),
                      );
                      setShowCopyMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-medical-700 dark:text-medical-300 hover:bg-medical-50 dark:hover:bg-medical-700 transition-colors"
                  >
                    <span className="font-medium">Shown events — summaries</span>
                    <p className="text-xs text-medical-500 dark:text-medical-400 mt-0.5">Date, type, and headline ({sortedFilteredEvents.length} events)</p>
                  </button>
                  {/* Shown events — full */}
                  <button
                    onClick={() => {
                      copyToClipboard(
                        formatFullChronology(sortedFilteredEvents),
                        formatFullChronologyHtml(sortedFilteredEvents),
                      );
                      setShowCopyMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-medical-700 dark:text-medical-300 hover:bg-medical-50 dark:hover:bg-medical-700 transition-colors"
                  >
                    <span className="font-medium">Shown events — full</span>
                    <p className="text-xs text-medical-500 dark:text-medical-400 mt-0.5">With detailed descriptions ({sortedFilteredEvents.length} events)</p>
                  </button>
                  {/* Word table — shown */}
                  <button
                    onClick={() => {
                      copyToClipboard(
                        formatChronologyTablePlain(sortedFilteredEvents),
                        formatChronologyTableHtml(sortedFilteredEvents),
                      );
                      setShowCopyMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-medical-700 dark:text-medical-300 hover:bg-medical-50 dark:hover:bg-medical-700 transition-colors"
                  >
                    <span className="font-medium">Word table — shown</span>
                    <p className="text-xs text-medical-500 dark:text-medical-400 mt-0.5">Table for Word / Google Docs ({sortedFilteredEvents.length} events)</p>
                  </button>
                  {/* All events — only show separator + options when filters are active */}
                  {sortedFilteredEvents.length < events.length && (
                    <>
                      <div className="border-t border-medical-100 dark:border-medical-700 my-1" />
                      <button
                        onClick={() => {
                          copyToClipboard(
                            formatFullChronology(sortedAllEvents),
                            formatFullChronologyHtml(sortedAllEvents),
                          );
                          setShowCopyMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-medical-700 dark:text-medical-300 hover:bg-medical-50 dark:hover:bg-medical-700 transition-colors"
                      >
                        <span className="font-medium">All events — full</span>
                        <p className="text-xs text-medical-500 dark:text-medical-400 mt-0.5">Ignores filters ({events.length} events)</p>
                      </button>
                      <button
                        onClick={() => {
                          copyToClipboard(
                            formatChronologyTablePlain(sortedAllEvents),
                            formatChronologyTableHtml(sortedAllEvents),
                          );
                          setShowCopyMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-medical-700 dark:text-medical-300 hover:bg-medical-50 dark:hover:bg-medical-700 transition-colors"
                      >
                        <span className="font-medium">All events — Word table</span>
                        <p className="text-xs text-medical-500 dark:text-medical-400 mt-0.5">Table ignoring filters ({events.length} events)</p>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Row B: View toggle | Significance pills | Sort toggle */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-3">
            {/* View mode toggle */}
            <div className="flex items-center bg-medical-100 dark:bg-medical-800 rounded-lg p-0.5">
              {timelineData?.timeline?.narrative && (
                <Tooltip text="AI-generated narrative summary of the chronology">
                  <button
                    onClick={() => setViewMode('summary')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      viewMode === 'summary'
                        ? 'bg-white dark:bg-medical-700 text-primary-700 dark:text-primary-400 shadow-sm'
                        : 'text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-300'
                    }`}
                  >
                    <BookOpen className="h-4 w-4" />
                    <span>Narrative</span>
                  </button>
                </Tooltip>
              )}
              <Tooltip text="Browse all individual chronology events">
                <button
                  onClick={() => setViewMode('graphical')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'graphical'
                      ? 'bg-white dark:bg-medical-700 text-primary-700 dark:text-primary-400 shadow-sm'
                      : 'text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-300'
                  }`}
                >
                  <BarChart3 className="h-4 w-4" />
                  <span>All Events</span>
                </button>
              </Tooltip>
            </div>

            <div className="h-4 w-px bg-medical-300 dark:bg-medical-600" />

            {/* Significance pills */}
            <div className="flex items-center gap-1.5">
              {([
                { key: 'MAJOR', label: 'Key', count: significanceCounts.MAJOR, dot: 'bg-red-500', tooltip: 'Key events — clinically or legally significant findings' },
                { key: 'SUPPORTING', label: 'Supporting', count: significanceCounts.SUPPORTING, dot: 'bg-amber-400', tooltip: 'Supporting events — relevant context and background' },
                { key: 'ROUTINE', label: 'Routine', count: significanceCounts.ROUTINE, dot: 'bg-gray-300 dark:bg-gray-500', tooltip: 'Routine events — standard appointments and minor notes' },
              ] as const).map(({ key, label, count, dot, tooltip }) => {
                const active = enabledSignificance.has(key);
                return (
                  <Tooltip key={key} text={tooltip}>
                    <button
                      onClick={() => {
                        const next = new Set(enabledSignificance);
                        if (active) { next.delete(key); } else { next.add(key); }
                        setEnabledSignificance(next);
                      }}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors border ${
                        active
                          ? 'border-medical-300 dark:border-medical-600 text-medical-700 dark:text-medical-300 bg-medical-50 dark:bg-medical-800'
                          : 'border-medical-200 dark:border-medical-700 text-medical-400 dark:text-medical-500 bg-transparent line-through'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${active ? dot : 'bg-medical-300 dark:bg-medical-600'}`} />
                      {label} {count}
                    </button>
                  </Tooltip>
                );
              })}
              {/* Child events toggle */}
              <Tooltip text="Nested entries grouped under a parent event">
                <button
                  onClick={() => setShowChildEvents(!showChildEvents)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors border ${
                    showChildEvents
                      ? 'border-medical-300 dark:border-medical-600 text-medical-700 dark:text-medical-300 bg-medical-50 dark:bg-medical-800'
                      : 'border-medical-200 dark:border-medical-700 text-medical-400 dark:text-medical-500 bg-transparent line-through'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${showChildEvents ? 'bg-cyan-400' : 'bg-medical-300 dark:bg-medical-600'}`} />
                  Sub-events
                </button>
              </Tooltip>
              {/* Expert evidence toggle */}
              {expertDocIds.size > 0 && (
                <Tooltip text="Events sourced from expert reports and opinions">
                  <button
                    onClick={() => setShowExpertEvidence(!showExpertEvidence)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors border ${
                      showExpertEvidence
                        ? 'border-amber-300 dark:border-amber-600 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30'
                        : 'border-medical-200 dark:border-medical-700 text-medical-400 dark:text-medical-500 bg-transparent line-through'
                    }`}
                  >
                    <Scale className="w-3 h-3" />
                    Expert {expertEventCount}
                  </button>
                </Tooltip>
              )}
            </div>

            <div className="h-4 w-px bg-medical-300 dark:bg-medical-600" />

            {/* Sort inline toggle */}
            <div className="flex items-center bg-medical-100 dark:bg-medical-800 rounded-lg p-0.5">
              <ArrowUpDown className="h-3.5 w-3.5 ml-1.5 text-medical-400 dark:text-medical-500" />
              {(Object.entries(SORT_LABELS) as [SortMode, string][]).map(([key, label]) => (
                <Tooltip key={key} text={key === 'date-desc' ? 'Most recent first' : 'Earliest first'}>
                  <button
                    onClick={() => setSortMode(key)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                      sortMode === key
                        ? 'bg-white dark:bg-medical-700 text-primary-700 dark:text-primary-400 shadow-sm'
                        : 'text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-300'
                    }`}
                  >
                    {label}
                  </button>
                </Tooltip>
              ))}
            </div>
          </div>

          {/* Query Results Banner */}
          {queryResult && (
            <ChronologyQueryResults
              result={queryResult}
              totalEventCount={events.length}
              onClear={clearQueryResult}
            />
          )}

          {/* Timeline */}
          {events.length === 0 ? (
            <div className="card">
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-medical-300 dark:text-medical-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">No Timeline Events</h3>
                <p className="text-medical-600 dark:text-medical-400 mb-4">
                  This case doesn't have any chronology events yet. Upload and process documents to generate a timeline.
                </p>
              </div>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="card">
              <div className="text-center py-8">
                <Filter className="h-12 w-12 text-medical-300 dark:text-medical-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">No Events Match Filters</h3>
                <p className="text-medical-600 dark:text-medical-400 mb-4">
                  Try enabling more event types in the sidebar.
                </p>
              </div>
            </div>
          ) : queryResult && queryFilteredEvents.length === 0 ? (
            <div className="card">
              <div className="text-center py-8">
                <Search className="h-12 w-12 text-medical-300 dark:text-medical-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-medical-900 dark:text-medical-100 mb-2">No Events Match Query</h3>
                <p className="text-medical-600 dark:text-medical-400 mb-4">
                  {queryResult.summary || 'No events matched your query. Try a different search.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Gaps and Conflicts Summary */}
              {(gaps.length > 0 || conflicts.length > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {gaps.length > 0 && (
                    <div className="gap-indicator">
                      <h4 className="font-medium mb-2">Temporal Gaps ({gaps.length})</h4>
                      <div className="space-y-1 text-sm">
                        {gaps.slice(0, 3).map((gap, index) => (
                          <div key={index}>
                            {gap.duration} gap ({gap.severity})
                          </div>
                        ))}
                        {gaps.length > 3 && (
                          <div className="text-xs">+{gaps.length - 3} more gaps</div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {conflicts.length > 0 && (
                    <div className="contradiction-highlight">
                      <h4 className="font-medium mb-2">Event Conflicts ({conflicts.length})</h4>
                      <div className="space-y-1 text-sm">
                        {conflicts.slice(0, 3).map((conflict, index) => (
                          <div key={index}>
                            {conflict.description} ({conflict.severity})
                          </div>
                        ))}
                        {conflicts.length > 3 && (
                          <div className="text-xs">+{conflicts.length - 3} more conflicts</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* View mode: Summary or Graphical */}
              {viewMode === 'summary' && timelineData?.timeline?.narrative ? (
                <ChronologyNarrativeView
                  caseId={caseId}
                  narrative={timelineData.timeline.narrative}
                  events={queryFilteredEvents}
                  totalEventCount={events.length}
                  formatEventDate={formatEventDate}
                  getEventTypeBadge={getEventTypeBadge}
                  getEventTypeBadgeColor={getEventTypeBadgeColor}
                  stripTypePrefix={stripTypePrefix}
                  onViewSource={handleViewDocument}
                  onSwitchToTimeline={() => setViewMode('graphical')}
                  monthRefs={monthRefs.current}
                  sortMode={sortMode}
                  onExplain={toggleExplain}
                  explainEventId={explainEventId}
                  explainLoading={explainLoading}
                  eventAnnotations={eventAnnotations}
                  eventSourceSummaries={eventSourceSummaries}
                  submitAskMessage={submitAskMessage}
                />
              ) : queryResult && (queryResult.intent === 'FILTER' || queryResult.intent === 'SORT') ? (
                /* Flat list for FILTER/SORT query results */
                <div className="relative mt-4">
                  <div className="absolute left-[20px] top-0 bottom-0 w-px bg-medical-200 dark:bg-medical-700"></div>
                  <div className="space-y-4">
                    {groupedEvents.map((event: any, index: number) => (
                      <div key={event.eventId || index}>
                        {renderEvent(event, index, true)}
                      </div>
                    ))}
                  </div>
                </div>
              ) : isDateSort ? (
                <>
                  {/* Events Timeline by Month */}
                  {monthGroups.map((monthGroup) => {
                    const monthEvents = groupedEvents.filter((event: any) => {
                      const raw = getEventDateStr(event.timestamp);
                      if (!raw) return false;
                      const eventDate = new Date(raw);
                      if (isNaN(eventDate.getTime())) return false;
                      return eventDate.getFullYear() === monthGroup.year && eventDate.getMonth() === monthGroup.month;
                    });

                    if (monthEvents.length === 0) return null;

                    return (
                      <div
                        key={monthGroup.key}
                        ref={(el) => {
                          if (el) monthRefs.current.set(monthGroup.key, el);
                        }}
                        className="scroll-mt-20"
                      >
                        {/* Sticky Month Header */}
                        <StickyMonthHeader
                          year={monthGroup.year}
                          month={monthGroup.month}
                          eventCount={monthGroup.count}
                          allMonths={monthGroups.map(g => ({
                            year: g.year,
                            month: g.month,
                            label: g.label,
                            count: g.count
                          }))}
                          onJumpTo={scrollToMonth}
                        />

                        {/* Month Events */}
                        <div className="relative mt-4 mb-10">
                          {/* Vertical timeline line */}
                          <div className="absolute left-[20px] top-0 bottom-0 w-px bg-medical-200 dark:bg-medical-700"></div>

                          <div className="space-y-4">
                            {monthEvents.map((parentEvent: any, parentIndex: number) => {
                              const hasChildren = parentEvent.children && parentEvent.children.length > 0;
                              const isExpanded = expandedEvents.has(parentEvent.eventId);

                              return (
                                <div key={parentEvent.eventId || parentIndex}>
                                  {/* Parent Event */}
                                  <div className="relative">
                                    {renderEvent(parentEvent, parentIndex, true)}

                                  </div>

                                  {/* Child Events */}
                                  {hasChildren && showChildEvents && (
                                    <div className="ml-12 mt-2 space-y-2 relative">
                                      {/* Child timeline line */}
                                      <div className="absolute left-[9px] top-0 bottom-0 w-px bg-medical-200 dark:bg-medical-700"></div>

                                      {parentEvent.children.map((childEvent: any, childIndex: number) => (
                                        <div key={childEvent.eventId || `${parentIndex}-${childIndex}`}>
                                          {renderChildEvent(childEvent, childIndex)}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : (
                /* Flat sorted list for non-date sorts */
                <div className="relative mt-4">
                  <div className="absolute left-[20px] top-0 bottom-0 w-px bg-medical-200 dark:bg-medical-700"></div>
                  <div className="space-y-4">
                    {groupedEvents.map((event: any, index: number) => (
                      <div key={event.eventId || index}>
                        {renderEvent(event, index, true)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Text selection popover for marking key findings */}
      {selectionPopover && (
        <TextSelectionPopover
          position={selectionPopover.position}
          onMark={() => {
            addFinding({
              eventId: selectionPopover.eventId,
              selectedText: selectionPopover.text,
              startOffset: selectionPopover.startOffset,
              endOffset: selectionPopover.endOffset,
            });
            setSelectionPopover(null);
            window.getSelection()?.removeAllRanges();
          }}
          onDismiss={() => setSelectionPopover(null)}
        />
      )}
    </div>
  );
};